import React, { useEffect, useState } from "react";
import {
  Group,
  Title,
  Text,
  Button,
  Badge,
  ActionIcon,
  Tooltip,
  Box,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconSettings,
  IconArrowLeft,
  IconCheck,
  IconLoader2,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import type { Menu, SaveStatus } from "./types";
import { BulkActionsMenu } from "./BulkActionsMenu";

interface Props {
  menu: Menu;
  saveStatus: SaveStatus;
  lastSavedAt: Date | null;
  onOpenSettings: () => void;
}

const formatRelative = (date: Date | null): string => {
  if (!date) return "";
  const diff = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

const SaveIndicator: React.FC<{ status: SaveStatus; lastSavedAt: Date | null }> = ({
  status,
  lastSavedAt,
}) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (status !== "saved") return;
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, [status]);

  if (status === "saving") {
    return (
      <Group gap={6} c="dimmed">
        <IconLoader2 size={14} className="mantine-rotate" />
        <Text size="sm">Saving…</Text>
      </Group>
    );
  }
  if (status === "error") {
    return (
      <Group gap={6} c="red">
        <IconAlertTriangle size={14} />
        <Text size="sm">Save failed</Text>
      </Group>
    );
  }
  if (status === "saved" || lastSavedAt) {
    return (
      <Group gap={6} c="dimmed">
        <IconCheck size={14} />
        <Text size="sm">Saved {formatRelative(lastSavedAt)}</Text>
      </Group>
    );
  }
  return null;
};

export const MenuHeader: React.FC<Props> = ({
  menu,
  saveStatus,
  lastSavedAt,
  onOpenSettings,
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const restaurantName = menu.restaurant?.name || "Menu";
  const pageCount = menu.menuPages?.length || 0;
  const itemCount = (menu.menuPages || []).reduce(
    (acc, p) => acc + (p.menuItems?.length || 0),
    0
  );

  return (
    <Box
      px={isMobile ? "sm" : "lg"}
      py="sm"
      style={{
        borderBottom: "1px solid var(--mantine-color-gray-3)",
        background: "var(--mantine-color-body)",
        position: "sticky",
        top: 0,
        zIndex: 5,
      }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <Tooltip label="Back to menus">
            <ActionIcon
              component={Link}
              to="/admin/menus"
              variant="subtle"
              color="gray"
              style={{ flexShrink: 0 }}
            >
              <IconArrowLeft size={18} />
            </ActionIcon>
          </Tooltip>
          <div style={{ minWidth: 0 }}>
            <Group gap="xs" wrap="nowrap">
              <Title order={4} fw={600} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {restaurantName}
              </Title>
              {!isMobile && (
                <Badge variant="light" color="orange" size="sm" style={{ flexShrink: 0 }}>
                  v2
                </Badge>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              {pageCount} {pageCount === 1 ? "page" : "pages"} · {itemCount}{" "}
              items
            </Text>
          </div>
        </Group>

        <Group gap={isMobile ? "xs" : "md"} wrap="nowrap" style={{ flexShrink: 0 }}>
          {!isMobile && <SaveIndicator status={saveStatus} lastSavedAt={lastSavedAt} />}
          {!isMobile && <BulkActionsMenu menuId={menu.id} />}
          <Tooltip label="Menu settings">
            <ActionIcon
              variant="default"
              onClick={onOpenSettings}
              size={isMobile ? "md" : "lg"}
            >
              <IconSettings size={isMobile ? 16 : 18} />
            </ActionIcon>
          </Tooltip>
          {!isMobile && (
            <Tooltip label="Open legacy editor">
              <Button
                component={Link}
                to={`/admin/menus/edit/${menu.id}`}
                variant="subtle"
                color="gray"
                size="sm"
              >
                Legacy
              </Button>
            </Tooltip>
          )}
        </Group>
      </Group>
      {isMobile && (
        <Group gap="xs" mt={4}>
          <SaveIndicator status={saveStatus} lastSavedAt={lastSavedAt} />
        </Group>
      )}
    </Box>
  );
};
